/**
 * Cheap auth probe: hit an endpoint that lives under the
 * auth-walled /v1/admin/* prefix and inspect the response.
 *
 * Auth flow on the cluster routes a 401/403 from oauth2-proxy
 * through the `oauth2-errors` middleware, which REWRITES the
 * response to serve the login-redirect HTML page. So:
 *
 *   logged in + authorised  → 200 + application/json
 *   not logged in OR not authorised → 200 + text/html (the redirect)
 *   network error          → exception
 *
 * Detect by content-type rather than status. Empty body + JSON
 * content-type is fine — we only care whether the request
 * round-tripped the full chain (oauth2-proxy + access-manager).
 *
 * The result is cached per page load; admin-link visibility doesn't
 * need to update mid-session.
 */
export interface AuthState {
  authorised: boolean;
}

const PROBE_URL = '/v1/admin/control/stats';

let cache: Promise<AuthState> | null = null;

export function checkAuth(): Promise<AuthState> {
  if (cache) return cache;
  cache = (async () => {
    try {
      const r = await fetch(PROBE_URL, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        // Don't follow any redirect that does slip through —
        // the answer is in this hop.
        redirect: 'manual',
      });
      const ct = r.headers.get('content-type') ?? '';
      return { authorised: r.ok && ct.includes('application/json') };
    } catch {
      return { authorised: false };
    }
  })();
  return cache;
}

/** Invalidate the cache — useful if we add a "log out" affordance later. */
export function resetAuthCache() {
  cache = null;
}
